import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type DMConversation, type DMMessage } from '@/lib/api';

export function useConversations() {
  return useQuery({
    queryKey: ['dm-conversations'],
    queryFn: async () => {
      const response = await api.get<DMConversation[]>('/dms/conversations');
      if (!response.success) throw new Error(response.error?.message || 'Failed to load conversations');
      return response.data || [];
    },
    staleTime: 30 * 1000,
  });
}

export function useMessages(conversationId: string) {
  return useQuery({
    queryKey: ['dm-messages', conversationId],
    queryFn: async () => {
      const response = await api.get<DMMessage[]>(`/dms/conversations/${conversationId}/messages`);
      if (!response.success) throw new Error(response.error?.message || 'Failed to load messages');
      return response.data || [];
    },
    enabled: !!conversationId,
    staleTime: 15 * 1000,
    refetchInterval: 15 * 1000,
  });
}

export function useSendMessage(conversationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (content: string) => {
      const response = await api.post<DMMessage>(`/dms/conversations/${conversationId}/messages`, { content });
      if (!response.success) throw new Error(response.error?.message || 'Failed to send message');
      return response.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dm-messages', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['dm-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['dm-unread'] });
    },
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: ['dm-unread'],
    queryFn: async () => {
      const response = await api.get<{ unread_count: number }>('/dms/unread');
      if (!response.success) return 0;
      return response.data?.unread_count || 0;
    },
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}
